export default {
    name: 'adstatisticsservice',
    dependencies: [],
    callable() {
        const propertyKeyAdFirstPublishedDate = 'firstPublished';
        const propertyKeyDefaultCounters = 'defaultCounters';
        const propertyKeyRepeatedVisitors = 'repeativeVisitsCounters';
        const propertyKeyTimeSeriesCounters = 'timeSeriesCounters';
        const propertyKeyCounters = 'counters';
        const propertyKeyCounterValues = 'dailyValues';
        const propertyKeyDescription = 'description';
        const propertyKeyAdViewsTotal = 'adViewsTotal';
        const propertyKeyCounterColor = 'rgbaColorValue';
        const propertyKeyDistributionVisitors = 'distributionVisitors';

        let _displayNLastDays;

        function createUrl(element) {
            const url = new URL('/ad/adstatistics.json', document.baseURI);
            ['finnkode', 'firstpublished', 'enddatetime', 's']
                .filter(name => element.hasAttribute(`data-${name}`))
                .forEach(name =>
                    url.searchParams.set(
                        name,
                        element.getAttribute(`data-${name}`)
                    )
                );
            return url;
        }

        function findNumberOfDaysToDisplay(
            nDaysSincePublished,
            displayNLastDays
        ) {
            return Math.min(nDaysSincePublished, displayNLastDays);
        }

        function findMaxXAxisValue(nDaysSincePublished) {
            const minimumDaysToDisplay = 7;
            return Math.max(nDaysSincePublished - 1, minimumDaysToDisplay - 1); // -1 to get zero-indexed value
        }

        function findMinXAxisValue(nDaysSincePublished, displayNLastDays) {
            return nDaysSincePublished - displayNLastDays;
        }

        function sumDailyValuesForPeriod(
            dailyValues,
            nDaysSincePublished,
            includeNLastDaysInPeriod
        ) {
            return Object.keys(dailyValues)
                .filter(iDay => {
                    const nDaysNotInPeriod =
                        nDaysSincePublished - includeNLastDaysInPeriod;
                    return iDay > nDaysNotInPeriod;
                })
                .reduce(
                    (previousValue, currentValue) =>
                        previousValue + dailyValues[currentValue],
                    0
                );
        }

        function createChartColumnDataFor(nElements, graphName, counter) {
            const dailyViews = new Array(nElements).fill(null);
            dailyViews[0] = graphName;

            const dailyViewsValues = counter[propertyKeyCounterValues];
            for (const iDay in dailyViewsValues) {
                if (dailyViewsValues.hasOwnProperty(iDay)) {
                    dailyViews[iDay] = dailyViewsValues[iDay];
                }
            }
            return dailyViews;
        }

        function findGraphColorsSortedByCounters(adStatistics) {
            const graphColorsSortedByCounters = {};
            const totalViewsCounterDescription =
                adStatistics[propertyKeyAdViewsTotal][propertyKeyDescription];
            graphColorsSortedByCounters[totalViewsCounterDescription] =
                adStatistics[propertyKeyAdViewsTotal][propertyKeyCounterColor];
            const timeSeriesCounters =
                adStatistics[propertyKeyTimeSeriesCounters][
                    propertyKeyCounters
                ];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                graphColorsSortedByCounters[
                    timeSeriesCounter[propertyKeyDescription]
                ] =
                    timeSeriesCounter[propertyKeyCounterColor];
            });
            return graphColorsSortedByCounters;
        }

        function createGraphGroup(timeSeriesCounters) {
            const graphGroup = [];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                graphGroup.push(timeSeriesCounter[propertyKeyDescription]);
            });
            return graphGroup;
        }

        function findDailyStatisticsSortedByCounters(
            timeSeriesCounters,
            nElements
        ) {
            const dailyStatisticsSortedByCounters = [];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                dailyStatisticsSortedByCounters.push(
                    createChartColumnDataFor(
                        nElements,
                        timeSeriesCounter[propertyKeyDescription],
                        timeSeriesCounter
                    )
                );
            });
            return dailyStatisticsSortedByCounters;
        }

        function findNDaysSincePublished(totalViewsCounter) {
            return Object.keys(totalViewsCounter[propertyKeyCounterValues])
                .length;
        }

        function createViewModelForChartLegend(
            adStatistics,
            nDaysSincePublished,
            displayNLastDays
        ) {
            const viewModel = {};
            let periodDescription = '';
            if (displayNLastDays === nDaysSincePublished) {
                periodDescription = 'i hele perioden';
            } else if (displayNLastDays === 14) {
                periodDescription = 'siste 14 dagene';
            } else if (displayNLastDays === 30) {
                periodDescription = 'siste 30 dagene';
            }

            viewModel.periodDescription = periodDescription;

            const counters =
                adStatistics[propertyKeyTimeSeriesCounters][
                    propertyKeyCounters
                ];
            const counterViewModels = counters.map(counter => ({
                description: counter.description,
                color: counter.rgbaColorValue,
                totalValue: sumDailyValuesForPeriod(
                    counter.dailyValues,
                    nDaysSincePublished,
                    displayNLastDays
                ),
                unit: counter.unit,
            }));

            const sumPeriod = counterViewModels.reduce(
                (previousValue, currentValue) =>
                    previousValue + currentValue.totalValue,
                0
            );

            viewModel.counters = counterViewModels;

            viewModel.sumPeriodValue = sumPeriod;
            viewModel.sumPeriodUnit = 'visninger';

            return viewModel;
        }

        function createViewModelForRepeatedVisitors(adStatistics) {
            const model = {};
            if (adStatistics[propertyKeyRepeatedVisitors] !== null) {
                model.repeativeVisits =
                    adStatistics[propertyKeyRepeatedVisitors];
                model.defaultStatistics =
                    adStatistics[propertyKeyDefaultCounters];
                model.distributionVisitors =
                    adStatistics[propertyKeyDistributionVisitors];
            }
            return model;
        }

        function createAndDisplayChart(
            chartElement,
            adStatistics,
            nDaysSincePublished,
            displayNLastDays
        ) {
            const monthAsText = [
                'jan',
                'feb',
                'mar',
                'apr',
                'mai',
                'jun',
                'jul',
                'aug',
                'sep',
                'okt',
                'nov',
                'des',
            ];

            const nElements = nDaysSincePublished + 1; // +1 because the name of the graph is in index 0.
            const timeSeriesCounters =
                adStatistics[propertyKeyTimeSeriesCounters][
                    propertyKeyCounters
                ];
            const dailyStatisticsSortedByCounters = findDailyStatisticsSortedByCounters(
                timeSeriesCounters,
                nElements
            );
            const graphGroup = createGraphGroup(timeSeriesCounters);
            const graphColorsSortedByCounters = findGraphColorsSortedByCounters(
                adStatistics
            );

            // HACK TO AVOID AN EMPTY CHART WITH NO AXIS.
            const dummyCounterName = 'dummyCounterToAvoidEmptyChart';
            dailyStatisticsSortedByCounters.push([dummyCounterName, 1]);
            graphGroup.push(dummyCounterName);
            graphColorsSortedByCounters[dummyCounterName] =
                'rgba(255, 255, 255, 0)';

            _displayNLastDays = displayNLastDays;
            const maxXAxisValue = findMaxXAxisValue(nDaysSincePublished);
            const minXAxisValue = findMinXAxisValue(
                nDaysSincePublished,
                displayNLastDays
            );
            const chart = c3.generate({
                bindto: chartElement,
                data: {
                    type: 'bar',
                    colors: graphColorsSortedByCounters,
                    groups: [graphGroup],
                    columns: dailyStatisticsSortedByCounters,
                    order: null, // Set to null to keep the order of the counters in the columns-data
                },
                legend: {
                    show: false,
                },
                tooltip: {
                    show: false,
                },
                point: {
                    show: false,
                },
                interaction: {
                    enabled: false,
                },
                grid: {
                    x: {
                        show: false,
                    },
                    y: {
                        show: true,
                    },
                },
                axis: {
                    x: {
                        height: 47,
                        tick: {
                            culling: false,
                            rotate: 60,
                            format(iDayZeroIndexed) {
                                const firstPublishedDate = new Date(
                                    adStatistics[
                                        propertyKeyAdFirstPublishedDate
                                    ]
                                );
                                const date = new Date(firstPublishedDate);
                                date.setDate(
                                    firstPublishedDate.getDate() +
                                        parseInt(iDayZeroIndexed)
                                );

                                const nTicksToDisplay = 7;
                                const showTickEveryNDay = Math.ceil(
                                    _displayNLastDays / nTicksToDisplay
                                );
                                const nDaysBeforeDisplayedRange =
                                    nDaysSincePublished - _displayNLastDays;
                                const isDayWithinDiplayedRange =
                                    iDayZeroIndexed >=
                                    nDaysBeforeDisplayedRange;
                                const shallDisplayTickLabel =
                                    iDayZeroIndexed %
                                        Math.floor(showTickEveryNDay) ===
                                    0;

                                if (
                                    isDayWithinDiplayedRange &&
                                    shallDisplayTickLabel
                                ) {
                                    return `${date.getDate()}. ${monthAsText[
                                        date.getMonth()
                                    ]}`;
                                }
                                return '';
                            },
                        },
                        min: minXAxisValue,
                        max: maxXAxisValue,
                    },
                    y: {
                        padding: { top: 7, bottom: 0 },
                        tick: {
                            format(yValue) {
                                return yValue === Math.floor(yValue)
                                    ? yValue
                                    : '';
                            },
                        },
                        min: 0,
                    },
                },
                bar: {
                    width: {
                        max: 35,
                    },
                },
            });

            // HACK TO HIDE X-AXIS TICK LABEL OUTSIDE CHART CANVAS.
            const elemFix = document.createElement('div');
            elemFix.id = 'xAxisTickLabelFix';
            elemFix.style.height = '40px';
            elemFix.style.width = '40px';
            elemFix.style.position = 'absolute';
            elemFix.style.left = '0px';
            elemFix.style.bottom = '0px';
            elemFix.style.background = 'white';
            elemFix.style.zIndex = '1';
            chartElement.appendChild(elemFix);

            return chart;
        }

        function changeChartXaxisRange(
            chart,
            nDaysSincePublished,
            displayNLastDays
        ) {
            _displayNLastDays = displayNLastDays;
            if (chart) {
                chart.axis.range({
                    max: { x: findMaxXAxisValue(nDaysSincePublished) },
                    min: {
                        x: findMinXAxisValue(
                            nDaysSincePublished,
                            displayNLastDays
                        ),
                    },
                });
            }
        }

        const exports = {};
        exports.createUrl = createUrl;
        exports.findNumberOfDaysToDisplay = findNumberOfDaysToDisplay;
        exports.findMaxXAxisValue = findMaxXAxisValue;
        exports.findMinXAxisValue = findMinXAxisValue;
        exports.sumDailyValuesForPeriod = sumDailyValuesForPeriod;
        exports.findNDaysSincePublished = findNDaysSincePublished;
        exports.createViewModelForChartLegend = createViewModelForChartLegend;
        exports.createViewModelForRepeatedVisitors = createViewModelForRepeatedVisitors;
        exports.createAndDisplayChart = createAndDisplayChart;
        exports.changeChartXaxisRange = changeChartXaxisRange;

        return exports;
    },
};



// WEBPACK FOOTER //
// ./services/adstatisticsservice.js